import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LeadListSidebar } from '@/components/inbox/LeadListSidebar';
import { ChatArea } from '@/components/inbox/ChatArea';
import { LeadProfileSidebar } from '@/components/inbox/LeadProfileSidebar';
import { useLeadsStore, normalizeLeadForUI } from '@/store/leadsStore';
import { useConversationsStore } from '@/store/conversationsStore';
import { useCannedResponsesStore } from '@/store/cannedResponsesStore';
import { useLojaAtivaStore } from '@/store/lojaAtivaStore';
import { api } from '@/services/api';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/authStore';
import type { Conversation, Lead } from '@/types';

// Selection is local UI state. The lead read endpoint below already persists both
// unread counters; the legacy store selector also POSTs (even for null), causing
// redundant writes on mount, lookup and close.
function selectConversation(id: string | null) {
  useConversationsStore.setState({ activeConversationId: id });
  if (id) useConversationsStore.getState().updateLocally?.(id, { unread_count: 0 });
}

export function Inbox() {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const leadIdFromUrl = urlParams.get('lead_id');

  const leads = useLeadsStore(state => state.leads);
  const fetchLeads = useLeadsStore(state => state.fetchLeads);
  const updateLeadLocally = useLeadsStore(state => state.updateLeadLocally);
  const setActiveLeadId = useLeadsStore(state => state.setActiveLeadId);

  const { conversations, activeConversationId, showArchived, setShowArchived } = useConversationsStore();
  const fetchCannedResponses = useCannedResponsesStore(state => state.fetchCannedResponses);
  const { lojaAtivaId } = useLojaAtivaStore();

  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [mobileView, setMobileView] = useState<'list' | 'chat' | 'profile'>('list');
  const [selectedTenantId, setSelectedTenantId] = useState<string>(() => localStorage.getItem('inbox_filter_tenant') || '');

  useEffect(() => {
    fetchLeads({ loja_id: lojaAtivaId || undefined });
    fetchCannedResponses();
  }, [fetchLeads, fetchCannedResponses, lojaAtivaId]);

  const authProfile = useAuthStore(state => state.profile);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [conversationError, setConversationError] = useState<string | null>(null);
  const [conversationRetry, setConversationRetry] = useState(0);

  // A Sidebar tem sua própria query: selecionar um lead também precisa hidratar sua conversa.
  // Cleanup descarta respostas tardias ao trocar lead, loja, tenant ou desmontar.
  useEffect(() => {
    let cancelled = false;
    setConversationError(null);
    if (!selectedLeadId) { selectConversation(null); setConversationLoading(false); return; }
    const profile = authProfile;
    const scopeValid = (found: any) => found?.lead_id === selectedLeadId &&
      !!profile && (profile.role === 'super_admin' || found.tenant_id === profile.tenant_id) &&
      (!lojaAtivaId || found.loja_id === lojaAtivaId) &&
      (profile.role !== 'super_admin' || !selectedTenantId || found.tenant_id === selectedTenantId);
    const local = conversations.find(c => c.lead_id === selectedLeadId && scopeValid(c));
    if (local) { setConversationLoading(false); selectConversation(local.id); return; }
    selectConversation(null);
    setConversationLoading(true);
    api.getConversationByLeadId(selectedLeadId).then(async found => {
      if (cancelled) return;
      if (!found) {
        // Lead sem conversa (ex.: importado por planilha, nunca conversou): abre chat
        // vazio; a conversa nasce no primeiro envio (POST /api/messages/send).
        if (!useLeadsStore.getState().leads.some(l => l.id === selectedLeadId)) {
          const lead = await api.getLeadById(selectedLeadId);
          if (cancelled) return;
          if (!scopeValid({ ...lead, lead_id: lead?.id })) throw new Error('lead fora da loja/empresa selecionada');
          useLeadsStore.getState().appendLeads([normalizeLeadForUI(lead)]);
        }
        return;
      }
      if (!scopeValid(found)) throw new Error('conversa fora da loja/empresa selecionada');
      useConversationsStore.setState(state => ({ conversations: state.conversations.some(c => c.id === found.id)
        ? state.conversations : [found, ...state.conversations] }));
      selectConversation(found.id);
    }).catch((err: any) => {
      if (cancelled) return;
      console.error('[INBOX_OPEN] Falha ao carregar conversa do lead', selectedLeadId, err);
      setConversationError(`Não foi possível carregar esta conversa (${err?.message || 'erro desconhecido'}). Tente novamente.`);
    }).finally(() => { if (!cancelled) setConversationLoading(false); });
    return () => { cancelled = true; };
    // A inserção no store não reinicia a requisição em andamento.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLeadId, lojaAtivaId, selectedTenantId, conversationRetry, authProfile]);

  // Auto-selecionar conversa e lead quando lead_id vier na URL (query string)
  useEffect(() => {
    if (!leadIdFromUrl) return;

    setSelectedLeadId(leadIdFromUrl);
    setActiveLeadId(leadIdFromUrl);
    setMobileView('chat');
    void api.markLeadAsRead(leadIdFromUrl).catch(() => {});
    updateLeadLocally(leadIdFromUrl, { unread_count: 0 });

    // A hidratação comum acima também atende navegação por URL.

    setTimeout(() => {
      const el = document.querySelector(`[data-lead-id="${leadIdFromUrl}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadIdFromUrl]);

  // Nunca associar a conversa anteriormente ativa ao novo lead selecionado.
  const activeConversation = conversations.find(c =>
    c.id === activeConversationId && c.lead_id === selectedLeadId &&
    (!lojaAtivaId || c.loja_id === lojaAtivaId) && !!authProfile &&
    (authProfile.role === 'super_admin' ? (!selectedTenantId || c.tenant_id === selectedTenantId) : c.tenant_id === authProfile.tenant_id)
  ) || null;
  const rawSelectedLead = leads.find(l => l.id === selectedLeadId);
  const selectedLead = rawSelectedLead || (activeConversation?.lead ? normalizeLeadForUI(activeConversation.lead) : null);

  // Clear selection if current lead doesn't match active store (lojaAtivaId)
  useEffect(() => {
    if (selectedLead && lojaAtivaId && selectedLead.loja_id && selectedLead.loja_id !== lojaAtivaId) {
      setSelectedLeadId(null);
      setActiveLeadId(null);
      setMobileView('list');
    }
  }, [lojaAtivaId, selectedLead, setSelectedLeadId, setActiveLeadId]);

  const handleSelectLead = (id: string, clickedLead?: Lead, clickedConversation?: Conversation) => {
    const profile = useAuthStore.getState().profile;
    const liveLojaId = useLojaAtivaStore.getState().lojaAtivaId;
    // A conversa já veio da query autorizada da Sidebar. Revalidar o escopo
    // antes de hidratar o cache do pai evita outra listagem bloqueante no clique.
    if (clickedConversation) {
      if (!profile || clickedConversation.lead_id !== id || clickedLead?.id !== id ||
          (profile.role !== 'super_admin' && clickedConversation.tenant_id !== profile.tenant_id) ||
          (liveLojaId && clickedConversation.loja_id !== liveLojaId) ||
          (profile.role === 'super_admin' && selectedTenantId && clickedConversation.tenant_id !== selectedTenantId)) return;
    }
    if (clickedLead) {
      // O objeto vem da query autorizada da Sidebar, não do storage persistido.
      // Revalidar identidade/escopo imediatamente antes de hidratar o store do pai.
      if (!profile || clickedLead.id !== id ||
          (profile.role !== 'super_admin' && clickedLead.tenant_id !== profile.tenant_id) ||
          (liveLojaId && clickedLead.loja_id !== liveLojaId) ||
          (profile.role === 'super_admin' && selectedTenantId && clickedLead.tenant_id !== selectedTenantId)) return;
      if (!useLeadsStore.getState().leads.some(l => l.id === id)) {
        useLeadsStore.getState().appendLeads([normalizeLeadForUI(clickedLead)]);
      }
    }
    if (clickedConversation) {
      useConversationsStore.setState(state => ({
        conversations: [clickedConversation, ...state.conversations.filter(c => c.id !== clickedConversation.id)]
      }));
      // Também cancela a hidratação pendente por URL ao clicar no mesmo lead.
      setConversationRetry(n => n + 1);
    }
    setSelectedLeadId(id);
    setActiveLeadId(id);
    setMobileView('chat');
    // Falha na leitura não bloqueia seleção/composição e não vira promise rejeitada invisível.
    void api.markLeadAsRead(id).catch(err => console.warn('[INBOX_READ] Falha ao marcar conversa como lida:', err));
    updateLeadLocally(id, { unread_count: 0 });


  };

  const handleBackToList = () => {
    setSelectedLeadId(null);
    setActiveLeadId(null);
    selectConversation(null);
    setMobileView('list');
  };

  // IMPLEMENTAÇÃO 2 — Atalho Esc fecha conversa (volta pra lista)
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && (selectedLeadId || activeConversationId)) {
        e.preventDefault();
        handleBackToList();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [selectedLeadId, activeConversationId]);

  // IMPLEMENTAÇÃO 3 — Atalho Ctrl+K abre busca rápida
  useEffect(() => {
    const handleCtrlK = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        // Procura o input de busca na tela e dá focus nele
        const searchInput = document.querySelector('input[placeholder*="Pesquisar"], input[placeholder*="Buscar"]') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
          searchInput.select(); // seleciona o texto existente se houver
        }
      }
    };
    window.addEventListener('keydown', handleCtrlK);
    return () => window.removeEventListener('keydown', handleCtrlK);
  }, []);

  return (
    <div className="flex h-full bg-white dark:bg-[#0B141A] overflow-hidden transition-colors duration-300">
      {/* 3 Columns Layout */}
      
      {/* Col 1: Inbox List */}
      <div className={cn(
        "border-r border-gray-200 dark:border-gray-800 flex flex-col flex-shrink-0 bg-gray-50/30 dark:bg-transparent transition-all",
        "w-full md:w-[320px] lg:w-[360px]",
        mobileView !== 'list' && "hidden md:flex"
      )}>
        <LeadListSidebar 
          leads={leads} 
          selectedLeadId={selectedLeadId} 
          onSelectLead={handleSelectLead}
          showArchived={showArchived}
          onToggleArchived={setShowArchived}
          selectedTenantId={selectedTenantId}
          onTenantChange={setSelectedTenantId}
        />
      </div>

      {/* Col 2: Chat Area */}
      <div className={cn(
        "flex-1 flex flex-col min-w-0 bg-[#F0F2F5] dark:bg-[#0B141A] relative transition-colors",
        mobileView !== 'chat' && "hidden md:flex"
      )}>
        {selectedLeadId && (conversationLoading || conversationError) ? (
          <div role={conversationError ? 'alert' : 'status'} className="flex-1 flex flex-col items-center justify-center gap-3 p-4 text-center">
            <p>{conversationError || 'Carregando conversa…'}</p>
            {conversationError && <button type="button" onClick={() => setConversationRetry(n => n + 1)}>Tentar novamente</button>}
          </div>
        ) : selectedLead ? (
          <ChatArea 
            key={selectedLead.id}
            lead={selectedLead} 
            conversation={activeConversation}
            onBack={handleBackToList}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400 flex-col gap-4 p-4 text-center">
            <div className="w-16 h-16 bg-gray-200 dark:bg-gray-800 rounded-full flex items-center justify-center">
              <span className="text-2xl text-gray-400 dark:text-gray-500">💬</span>
            </div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Selecione uma conversa para iniciar o atendimento</p>
          </div>
        )}
      </div>
    </div>
  );
}
